<template>
  <div class="km-support">
    <!-- ===== TOP BAR ===== -->
    <header class="ks-top">
      <button class="ks-back" @click="goBack" aria-label="Back">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="ks-peer">
        <div class="ks-peer__avatar">
          <span>🎧</span>
          <span class="ks-peer__dot"></span>
        </div>
        <div class="ks-peer__body">
          <div class="ks-peer__name">KissMora Support <span class="ks-peer__tick">✓</span></div>
          <div class="ks-peer__status">
            <span v-if="agentTyping" class="ks-typing-ind"><span></span><span></span><span></span> typing…</span>
            <span v-else>● Online · we usually reply in a minute</span>
          </div>
        </div>
      </div>
      <button class="ks-icon" @click="showInfo = !showInfo" aria-label="Info">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
      </button>
    </header>

    <!-- ===== MESSAGES ===== -->
    <main class="ks-messages" ref="msgs">
      <!-- Welcome block -->
      <div class="ks-welcome">
        <div class="ks-welcome__badge">🎧</div>
        <h3>How can we help?</h3>
        <p>We're here 24/7 to help with anything on KissMora — account, payments, safety, or anything else.</p>
      </div>

      <!-- Alternative contact channels (populated from admin config) -->
      <div v-if="channels.length" class="ks-channels">
        <div class="ks-channels__head">
          <span>Or reach us directly</span>
          <small>{{ hoursLabel }}</small>
        </div>
        <div class="ks-channels__grid">
          <a
            v-for="c in channels" :key="c.key"
            :href="c.href" :target="c.target || '_self'" rel="noopener"
            class="ks-channel" :class="'ks-channel--'+c.key"
          >
            <span class="ks-channel__icon">{{ c.emoji }}</span>
            <div class="ks-channel__body">
              <strong>{{ c.label }}</strong>
              <small>{{ c.display }}</small>
            </div>
            <span class="ks-channel__chev">›</span>
          </a>
        </div>
      </div>

      <!-- Quick topics (first message) -->
      <div class="ks-topics" v-if="messages.length <= 1">
        <button v-for="t in topics" :key="t.key" class="ks-topic" @click="pickTopic(t)">
          <span class="ks-topic__emoji">{{ t.emoji }}</span>
          <span>{{ t.label }}</span>
        </button>
      </div>

      <!-- Message thread -->
      <template v-for="(block, bi) in groupedMessages">
        <div class="ks-date" :key="'d'+bi"><span>{{ block.date }}</span></div>
        <template v-for="(m, mi) in block.messages">
          <!-- System join banner -->
          <div v-if="m.sender === 'system'" :key="'m'+bi+'-'+mi" class="ks-system">
            <span class="ks-system__pill">🛎 {{ m.text }}</span>
          </div>
          <!-- Normal bubble -->
          <div v-else :key="'m'+bi+'-'+mi"
               class="ks-row"
               :class="[m.mine ? 'ks-row--mine' : 'ks-row--theirs', { 'ks-row--tail': m.showTail, 'ks-row--first': m.showAvatar }]">
            <div v-if="!m.mine && m.showAvatar" class="ks-row__avatar" :class="{ 'ks-row__avatar--agent': m.sender === 'admin' }">
              {{ m.sender === 'admin' ? agentInitial(m.adminName) : '🎧' }}
            </div>
            <div v-else-if="!m.mine" class="ks-row__avatar ks-row__avatar--ghost"></div>
            <div class="ks-bubble-wrap">
              <div v-if="!m.mine && m.showAvatar && m.sender === 'admin' && m.adminName"
                   class="ks-bubble__sender">{{ m.adminName }}</div>
              <div class="ks-bubble" :class="{'ks-bubble--agent': m.sender === 'admin'}">{{ m.text }}</div>
              <div v-if="m.showTime" class="ks-bubble__meta">
                {{ m.time }}
                <span v-if="m.mine && m.read" class="ks-bubble__read">✓✓</span>
                <span v-else-if="m.mine" class="ks-bubble__sent">✓</span>
              </div>
            </div>
          </div>
        </template>
      </template>

      <!-- Typing indicator -->
      <div v-if="agentTyping" class="ks-row ks-row--theirs ks-row--first">
        <div class="ks-row__avatar">🎧</div>
        <div class="ks-bubble-wrap">
          <div class="ks-bubble ks-bubble--typing">
            <span class="ks-typing"><span></span><span></span><span></span></span>
          </div>
        </div>
      </div>
    </main>

    <!-- ===== COMPOSER ===== -->
    <footer class="ks-composer">
      <button class="ks-icon ks-icon--attach" @click="soon" aria-label="Attach">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.49"/></svg>
      </button>
      <div class="ks-input">
        <textarea
          ref="input"
          v-model="draft"
          rows="1"
          placeholder="Type your message…"
          @keydown="onKeydown"
          @input="onInput"
        ></textarea>
      </div>
      <button v-if="draft.trim()" class="ks-send" @click="sendMessage" aria-label="Send">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
      </button>
      <button v-else class="ks-send ks-send--ghost" @click="soon" aria-label="Voice">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5.3-3a5.3 5.3 0 0 1-10.6 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11Z"/></svg>
      </button>
    </footer>

    <transition name="ks-info">
      <div v-if="showInfo" class="ks-info" @click.self="showInfo = false">
        <div class="ks-info__panel">
          <button class="ks-info__close" @click="showInfo = false">✕</button>
          <div class="ks-info__avatar">🎧</div>
          <h3>KissMora Support</h3>
          <p>Average response time: <strong>&lt; 2 min</strong></p>
          <div class="ks-info__rows">
            <div class="ks-info__row"><span>🕐</span> 24/7 availability</div>
            <div class="ks-info__row"><span>🔒</span> All chats encrypted</div>
            <div class="ks-info__row"><span>🌍</span> 12 languages supported</div>
          </div>
          <div class="ks-info__actions">
            <button class="ks-info__btn" @click="soon">📧 Email transcript</button>
            <button class="ks-info__btn" @click="soon">📋 Start new issue</button>
            <button class="ks-info__btn ks-info__btn--danger" @click="clearChat">🗑 Clear chat</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const TOPICS = [
  { key:'account',  emoji:'👤', label:"Account & login help" },
  { key:'payments', emoji:'💳', label:"Payments & top-up" },
  { key:'safety',   emoji:'🛡️', label:"Report a user" },
  { key:'verify',   emoji:'✓',  label:"Profile verification" },
  { key:'bug',      emoji:'🐞', label:"Something isn't working" },
  { key:'other',    emoji:'💬', label:"Something else" }
];

// Auto-replies now come from the server (km_support_faq, admin-editable).
// Topic chips just prefill the draft and send via the real backend.

export default {
  name: 'KmSupport',
  data(){
    return {
      draft: '',
      showInfo: false,
      agentTyping: false,
      topics: TOPICS,
      messages: [
        { mine:false, text:"Hi! 👋 I'm KissMora Support. Pick a topic below or type your message.", at: Date.now() - 1000, read: true }
      ]
    };
  },
  computed: {
    baseInfo(){
      return (this.$store && this.$store.getters && this.$store.getters.getBaseInfo) || {};
    },
    hoursLabel(){
      return String(this.baseInfo.cs_hours || '').trim() || '24/7 Dedicated Service';
    },
    channels(){
      const b = this.baseInfo || {};
      const items = [];

      const tg = String(b.cs_telegram || '').trim();
      if(tg){
        let href = tg;
        if(/^@/.test(tg)) href = 'https://t.me/' + tg.slice(1);
        else if(!/^https?:/i.test(tg) && !/^tg:/i.test(tg)) href = 'https://t.me/' + tg.replace(/^\/+/, '');
        items.push({ key:'telegram', emoji:'✈️', label:'Telegram', display: tg, href, target:'_blank' });
      }

      const wa = String(b.cs_whatsapp || '').trim();
      if(wa){
        const digits = wa.replace(/[^\d+]/g, '').replace(/^\+/, '');
        items.push({ key:'whatsapp', emoji:'💬', label:'WhatsApp', display: wa, href: 'https://wa.me/' + digits, target:'_blank' });
      }

      const em = String(b.cs_email || '').trim();
      if(em){
        items.push({ key:'email', emoji:'✉️', label:'Email', display: em, href: 'mailto:' + em });
      }

      const ph = String(b.cs_phone || '').trim();
      if(ph){
        items.push({ key:'phone', emoji:'📞', label:'Phone', display: ph, href: 'tel:' + ph.replace(/\s/g, '') });
      }

      const line = String(b.cs_line || '').trim();
      if(line){
        let href = line;
        if(!/^https?:/i.test(line)) href = 'https://line.me/ti/p/' + line.replace(/^@/, '');
        items.push({ key:'line', emoji:'💚', label:'LINE', display: line, href, target:'_blank' });
      }

      const disc = String(b.cs_discord || '').trim();
      if(disc){
        items.push({ key:'discord', emoji:'🎮', label:'Discord', display: disc, href: /^https?:/i.test(disc) ? disc : 'https://discord.gg/' + disc, target:'_blank' });
      }

      const kefu = String(b.kefu || '').trim();
      if(kefu && items.length === 0){
        // Last-resort generic live-chat URL the admin set under "kefu"
        items.push({ key:'web', emoji:'💻', label:'Live chat', display: 'Open live chat', href: kefu, target:'_blank' });
      }

      return items;
    },
    groupedMessages(){
      const msgs = this.messages.slice();
      if(!msgs.length) return [];
      const groups = [];
      let curDay = '';
      let group = null;
      msgs.forEach((m, i) => {
        const d = new Date(m.at);
        const dayLabel = this.dayLabel(d);
        if(dayLabel !== curDay){
          curDay = dayLabel;
          group = { date: dayLabel, messages: [] };
          groups.push(group);
        }
        const prev = i > 0 ? msgs[i-1] : null;
        const next = i < msgs.length - 1 ? msgs[i+1] : null;
        const showAvatar = !m.mine && (!prev || prev.mine !== m.mine);
        const showTail = !next || next.mine !== m.mine || (m.at && next.at && next.at - m.at > 5 * 60 * 1000);
        const showTime = showTail;
        group.messages.push({ ...m, showAvatar, showTail, showTime, time: this.timeLabel(d) });
      });
      return groups;
    }
  },
  methods: {
    dayLabel(d){
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const that = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const diff = Math.round((today - that) / 86400000);
      if(diff === 0) return 'Today';
      if(diff === 1) return 'Yesterday';
      if(diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
    timeLabel(d){ return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); },
    goBack(){ if(window.history.length > 1) this.$router.go(-1); else this.$router.push('/Mine'); },
    soon(){ this.$toast('Coming soon ✨'); },
    clearChat(){
      this.messages = [
        { mine:false, text:"Chat cleared. How can I help? 👋", at: Date.now(), read: true }
      ];
      this.showInfo = false;
      this.scrollBottom();
    },
    pickTopic(t){
      this.draft = t.label;
      this.sendMessage();
    },
    sendMessage(){
      const text = this.draft.trim();
      if(!text) return;
      // Optimistic render — show the user's message right away
      this.messages.push({ mine:true, text, at: Date.now(), read: false });
      this.draft = '';
      if(this.$refs.input){ this.$refs.input.style.height = 'auto'; }
      this.scrollBottom();

      // Typing indicator while the bot thinks
      this.agentTyping = true;
      this.$http({ method: 'post', url: 'chat_send', data: { body: text } }).then(res => {
        this.agentTyping = false;
        if (res && res.code === 200 && res.data) {
          if (res.data.escalated) {
            this.$toast && this.$toast('Ticket opened — a human agent will join soon 🎫');
          }
          this.syncServerMessages(res.data.messages || []);
        } else if (res && res.msg) {
          this.messages.push({ mine:false, text: res.msg, at: Date.now(), read: true, system: true });
        }
        // Mark all my messages as delivered
        this.messages.forEach(m => { if (m.mine) m.read = true; });
        this.scrollBottom();
      }).catch(() => {
        this.agentTyping = false;
        this.messages.push({ mine:false, text:"Network hiccup — please try again.", at: Date.now(), read: true });
        this.scrollBottom();
      });
    },

    /** Replace the visible transcript with the server's copy (includes bot + agent replies). */
    syncServerMessages(serverMsgs){
      if (!Array.isArray(serverMsgs)) return;
      const hadAgent = this.messages.some(m => m.sender === 'admin');
      this.messages = serverMsgs.map(m => ({
        mine:      m.sender === 'user',
        text:      m.body,
        at:        (Number(m.created_at) || 0) * 1000,
        read:      true,
        system:    m.sender === 'bot' || m.sender === 'system',
        sender:    m.sender,
        adminName: m.admin_name || ''
      }));
      // Toast the user the first time a real agent chimes in
      const nowHasAgent = this.messages.some(m => m.sender === 'admin');
      if (!hadAgent && nowHasAgent) {
        const agent = this.messages.find(m => m.sender === 'admin');
        const name = agent && agent.adminName ? agent.adminName : 'An agent';
        this.$toast && this.$toast(name + ' has joined the chat 👋');
      }
      this.scrollBottom();
    },
    agentInitial(name){
      if (!name) return '💜';
      return String(name).trim().charAt(0).toUpperCase();
    },

    /** Poll for new agent messages every 4s while the drawer is open. */
    startPolling(){
      if (this._pollT) return;
      const tick = () => {
        this.$http({ method: 'get', url: 'chat_messages' }).then(res => {
          if (res && res.code === 200 && Array.isArray(res.data)) {
            const srvCount = res.data.length;
            const localCount = this.messages.length;
            // Only overwrite if the server has MORE messages than we've got (avoids clobbering while typing)
            if (srvCount > localCount) this.syncServerMessages(res.data);
          }
        });
      };
      tick();
      this._pollT = setInterval(tick, 4000);
    },
    stopPolling(){ if (this._pollT) { clearInterval(this._pollT); this._pollT = null; } },
    runAgentReplies(lines){
      clearTimeout(this._typingT);
      clearTimeout(this._sendT);
      const send = (i) => {
        if(i >= lines.length){
          this.agentTyping = false;
          // mark mine messages as read
          this.messages.forEach(m => { if(m.mine) m.read = true; });
          return;
        }
        this.agentTyping = true;
        this.scrollBottom();
        this._sendT = setTimeout(() => {
          this.agentTyping = false;
          this.messages.push({ mine:false, text: lines[i], at: Date.now(), read: true });
          this.scrollBottom();
          this._typingT = setTimeout(() => send(i + 1), 500);
        }, 1100 + Math.min(1800, lines[i].length * 18));
      };
      this._typingT = setTimeout(() => send(0), 500);
    },
    onInput(e){
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = Math.min(120, el.scrollHeight) + 'px';
    },
    onKeydown(e){
      if(e.key === 'Enter' && !e.shiftKey){ e.preventDefault(); this.sendMessage(); }
    },
    scrollBottom(){
      this.$nextTick(() => {
        const el = this.$refs.msgs;
        if(el) el.scrollTop = el.scrollHeight;
      });
    }
  },
  created(){
    if(!localStorage.getItem('token')){ this.$router.replace('/Login'); return; }
    // Open / create the thread on the server and pull existing transcript
    this.$http({ method: 'get', url: 'chat_open' }).then(() => {
      this.$http({ method: 'get', url: 'chat_messages' }).then(res => {
        if (res && res.code === 200 && Array.isArray(res.data) && res.data.length) {
          this.syncServerMessages(res.data);
        }
      });
    });
  },
  mounted(){ this.scrollBottom(); this.startPolling(); },
  beforeDestroy(){
    clearTimeout(this._typingT);
    clearTimeout(this._sendT);
    this.stopPolling();
  }
};
</script>

<style scoped>
.km-support {
  position: fixed; inset: 0; z-index: 5;
  display: flex; flex-direction: column;
  background: linear-gradient(180deg, #fdf5f9 0%, #f6eef3 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #2a1a3f;
  overflow: hidden;
}
.km-support * { box-sizing: border-box; }
button { font-family: inherit; cursor: pointer; }

/* Top bar */
.ks-top {
  flex-shrink: 0;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(22px) saturate(170%);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  border-bottom: 1px solid rgba(255,77,141,.1);
}
.ks-back, .ks-icon {
  width: 40px; height: 40px; border-radius: 12px;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  display: flex; align-items: center; justify-content: center;
  color: #2a1a3f; transition: border-color .2s, transform .15s;
}
.ks-back:hover, .ks-icon:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.ks-peer { flex: 1; min-width: 0; display: flex; align-items: center; gap: 10px; }
.ks-peer__avatar {
  position: relative; width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  box-shadow: 0 6px 14px rgba(255,77,141,.35);
}
.ks-peer__dot {
  position: absolute; bottom: 0; right: 0;
  width: 12px; height: 12px; border-radius: 50%; background: #10b981; border: 2px solid #fff;
}
.ks-peer__body { flex: 1; min-width: 0; }
.ks-peer__name {
  font-size: 15px; font-weight: 800; color: #2a1a3f;
  display: flex; align-items: center; gap: 5px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ks-peer__tick {
  width: 16px; height: 16px; border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff; font-size: 9px; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
}
.ks-peer__status { font-size: 12px; color: #10b981; font-weight: 500; }

.ks-typing-ind { color: #ff4d8d; font-weight: 600; font-style: italic; display: inline-flex; align-items: center; gap: 4px; }
.ks-typing-ind > span {
  width: 4px; height: 4px; border-radius: 50%; background: currentColor; display: inline-block;
  animation: ksDot 1.2s ease-in-out infinite;
}
.ks-typing-ind > span:nth-child(2) { animation-delay: .15s; }
.ks-typing-ind > span:nth-child(3) { animation-delay: .3s; }
@keyframes ksDot {
  0%, 60%, 100% { transform: scale(.6); opacity: .3; }
  30% { transform: scale(1.3); opacity: 1; }
}

/* Messages */
.ks-messages { flex: 1; overflow-y: auto; padding: 16px 14px 8px; scroll-behavior: smooth; }
.ks-messages::-webkit-scrollbar { width: 6px; }
.ks-messages::-webkit-scrollbar-thumb { background: rgba(26,16,51,.15); border-radius: 3px; }

.ks-welcome {
  text-align: center; padding: 20px 24px; margin-bottom: 16px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 20px;
  border: 1px solid rgba(255,77,141,.12);
  box-shadow: 0 6px 18px rgba(255,77,141,.08);
}
.ks-welcome__badge {
  width: 56px; height: 56px; border-radius: 18px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  margin: 0 auto 12px; font-size: 28px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 10px 24px rgba(255,77,141,.35);
}
.ks-welcome h3 { margin: 0 0 4px; font-size: 18px; font-weight: 800; }
.ks-welcome p { margin: 0; font-size: 13px; color: #6b5c85; line-height: 1.5; }

/* Alternative contact channels */
.ks-channels {
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1px solid rgba(255,77,141,.14);
  border-radius: 20px;
  padding: 14px 16px 12px;
  box-shadow: 0 6px 18px rgba(255,77,141,.08), inset 0 1px 0 rgba(255,255,255,.9);
  margin-bottom: 16px;
}
.ks-channels__head {
  display: flex; align-items: baseline; justify-content: space-between;
  padding: 0 2px 10px;
}
.ks-channels__head span {
  font-size: 12px; font-weight: 800; color: #8273a0;
  letter-spacing: .06em; text-transform: uppercase;
}
.ks-channels__head small { font-size: 11px; color: #10b981; font-weight: 700; }
.ks-channels__grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
}
.ks-channel {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; border-radius: 14px;
  background: #fff; border: 1.5px solid rgba(255,77,141,.12);
  text-decoration: none; color: #2a1a3f;
  font-family: inherit;
  transition: transform .15s, border-color .2s, box-shadow .25s;
}
.ks-channel:hover {
  transform: translateY(-2px);
  border-color: #ff4d8d;
  box-shadow: 0 10px 22px rgba(255,77,141,.15);
}
.ks-channel__icon {
  width: 38px; height: 38px; border-radius: 12px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 18px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  box-shadow: 0 4px 10px rgba(255,77,141,.3);
}
/* Per-channel accent colors */
.ks-channel--telegram .ks-channel__icon { background: linear-gradient(135deg,#60a5fa,#3b82f6); box-shadow: 0 4px 10px rgba(59,130,246,.35); }
.ks-channel--whatsapp .ks-channel__icon { background: linear-gradient(135deg,#34d399,#059669); box-shadow: 0 4px 10px rgba(5,150,105,.35); }
.ks-channel--email    .ks-channel__icon { background: linear-gradient(135deg,#c084fc,#a020f0); box-shadow: 0 4px 10px rgba(160,32,240,.35); }
.ks-channel--phone    .ks-channel__icon { background: linear-gradient(135deg,#fbbf24,#f59e0b); box-shadow: 0 4px 10px rgba(245,158,11,.35); }
.ks-channel--line     .ks-channel__icon { background: linear-gradient(135deg,#4ade80,#16a34a); box-shadow: 0 4px 10px rgba(22,163,74,.35); }
.ks-channel--discord  .ks-channel__icon { background: linear-gradient(135deg,#818cf8,#5b21b6); box-shadow: 0 4px 10px rgba(91,33,182,.35); }
.ks-channel--web      .ks-channel__icon { background: linear-gradient(135deg,#fda4af,#f43f5e); box-shadow: 0 4px 10px rgba(244,63,94,.35); }
.ks-channel__body { flex: 1; min-width: 0; }
.ks-channel__body strong { display: block; font-size: 13px; font-weight: 800; color: #2a1a3f; }
.ks-channel__body small {
  display: block; font-size: 11px; color: #8273a0; margin-top: 1px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ks-channel__chev { color: #b8a9c9; font-size: 18px; line-height: 1; }

.ks-topics { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
.ks-topic {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; border: 1.5px solid rgba(255,77,141,.18);
  background: #fff; border-radius: 14px;
  font-family: inherit; font-size: 13px; font-weight: 600; color: #2a1a3f;
  text-align: left; cursor: pointer;
  transition: transform .15s, border-color .2s, background .2s;
}
.ks-topic:hover {
  transform: translateY(-2px); border-color: #ff4d8d;
  background: linear-gradient(135deg, rgba(255,77,141,.06), rgba(160,32,240,.06));
}
.ks-topic__emoji { font-size: 18px; flex-shrink: 0; }

.ks-date { display: flex; justify-content: center; margin: 16px 0 10px; }
.ks-date span {
  font-size: 11px; font-weight: 700; color: #8273a0;
  letter-spacing: .1em; text-transform: uppercase;
  padding: 4px 12px; border-radius: 999px;
  background: rgba(255,77,141,.08);
}

.ks-row { display: flex; gap: 8px; align-items: flex-end; margin-bottom: 2px; }
.ks-row--theirs { justify-content: flex-start; }
.ks-row--mine { justify-content: flex-end; }
.ks-row--first { margin-top: 8px; }
.ks-row__avatar {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px;
  box-shadow: 0 4px 10px rgba(255,77,141,.3);
}
.ks-row__avatar--ghost { visibility: hidden; }
.ks-row__avatar--agent {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff; font-size: 15px; font-weight: 800;
  box-shadow: 0 6px 14px rgba(16,185,129,.35);
}

/* System / agent-join banner */
.ks-system { display: flex; justify-content: center; margin: 14px 0; }
.ks-system__pill {
  padding: 6px 14px; border-radius: 999px;
  background: linear-gradient(135deg, rgba(16,185,129,.15), rgba(5,150,105,.15));
  color: #047857; font-size: 12px; font-weight: 700;
  border: 1px solid rgba(16,185,129,.3);
  box-shadow: 0 2px 6px rgba(16,185,129,.12);
}

/* Agent name above their bubble */
.ks-bubble__sender {
  font-size: 11px; font-weight: 800; color: #047857;
  margin: 0 4px 4px; letter-spacing: .02em;
}
.ks-bubble--agent {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0) !important;
  color: #064e3b !important;
  border: 1px solid rgba(16,185,129,.25) !important;
}

.ks-bubble-wrap { display: flex; flex-direction: column; min-width: 0; max-width: 76%; }
.ks-row--mine .ks-bubble-wrap { align-items: flex-end; }
.ks-bubble {
  padding: 10px 14px; border-radius: 20px;
  font-size: 14px; line-height: 1.45;
  word-wrap: break-word; word-break: break-word;
}
.ks-row--theirs .ks-bubble {
  background: #fff; color: #2a1a3f;
  border: 1px solid rgba(255,77,141,.1);
  border-bottom-left-radius: 6px;
  box-shadow: 0 2px 8px rgba(26,16,51,.04);
}
.ks-row--mine .ks-bubble {
  background: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
  color: #fff; border-bottom-right-radius: 6px;
  box-shadow: 0 4px 14px rgba(255,77,141,.25);
}
.ks-bubble__meta {
  font-size: 11px; color: #8273a0; margin: 4px 10px 8px;
  display: flex; align-items: center; gap: 4px;
}
.ks-bubble__read { color: #3b82f6; font-weight: 800; }
.ks-bubble__sent { color: #b8a9c9; }
.ks-bubble--typing { padding: 12px 16px; }
.ks-typing { display: inline-flex; align-items: center; gap: 4px; }
.ks-typing > span {
  width: 6px; height: 6px; border-radius: 50%; background: #b8a9c9;
  animation: ksDot 1.2s ease-in-out infinite;
}
.ks-typing > span:nth-child(2) { animation-delay: .15s; }
.ks-typing > span:nth-child(3) { animation-delay: .3s; }

/* Composer */
.ks-composer {
  flex-shrink: 0;
  display: flex; align-items: flex-end; gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0));
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(22px) saturate(170%);
  -webkit-backdrop-filter: blur(22px) saturate(170%);
  border-top: 1px solid rgba(255,77,141,.1);
}
.ks-icon--attach {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  color: #a020f0; display: flex; align-items: center; justify-content: center;
  transition: border-color .2s, transform .15s;
}
.ks-icon--attach:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.ks-input {
  flex: 1; min-width: 0;
  display: flex; align-items: flex-end;
  background: #fff; border: 1.5px solid rgba(255,77,141,.14);
  border-radius: 22px;
  padding: 8px 14px;
  transition: border-color .2s, box-shadow .2s;
}
.ks-input:focus-within { border-color: #ff4d8d; box-shadow: 0 0 0 4px rgba(255,77,141,.1); }
.ks-input textarea {
  flex: 1; border: none; outline: none; resize: none;
  background: transparent; color: #2a1a3f;
  font-family: inherit; font-size: 14px; line-height: 1.4;
  padding: 4px 0; max-height: 120px;
}
.ks-input textarea::placeholder { color: #b8a9c9; }
.ks-send {
  width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0; border: none;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 18px rgba(255,77,141,.35);
  transition: transform .15s, box-shadow .2s;
}
.ks-send:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(255,77,141,.5); }
.ks-send--ghost {
  background: #fff; color: #a020f0;
  border: 1.5px solid rgba(255,77,141,.25); box-shadow: 0 4px 10px rgba(26,16,51,.06);
}
.ks-send--ghost:hover { border-color: #ff4d8d; box-shadow: 0 8px 16px rgba(255,77,141,.2); }

/* Info drawer */
.ks-info {
  position: fixed; inset: 0; z-index: 20;
  background: rgba(26,16,51,.45); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.ks-info__panel {
  position: relative;
  width: 100%; max-width: 400px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 24px; padding: 32px 26px 22px;
  box-shadow: 0 30px 70px rgba(0,0,0,.3);
  text-align: center;
}
.ks-info__close {
  position: absolute; top: 14px; right: 14px;
  width: 32px; height: 32px; border-radius: 50%; border: none;
  background: rgba(26,16,51,.06); color: #6b5c85; font-size: 16px;
}
.ks-info__avatar {
  width: 64px; height: 64px; border-radius: 20px; margin: 0 auto 10px;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  font-size: 28px; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 10px 24px rgba(255,77,141,.35);
}
.ks-info__panel h3 { margin: 0 0 4px; font-size: 20px; font-weight: 800; }
.ks-info__panel > p { margin: 0 0 16px; font-size: 13px; color: #6b5c85; }
.ks-info__rows {
  background: rgba(255,77,141,.06); border-radius: 14px;
  padding: 10px 14px; margin-bottom: 18px;
}
.ks-info__row {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 0; font-size: 13px; font-weight: 600; color: #4a3d66;
  border-bottom: 1px solid rgba(255,77,141,.08);
}
.ks-info__row:last-child { border-bottom: none; }
.ks-info__actions { display: flex; flex-direction: column; gap: 8px; }
.ks-info__btn {
  width: 100%; padding: 11px; border: none; border-radius: 12px; cursor: pointer;
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  color: #a020f0; font-weight: 700; font-size: 13px;
}
.ks-info__btn:hover { background: linear-gradient(135deg, rgba(255,77,141,.15), rgba(160,32,240,.15)); }
.ks-info__btn--danger { color: #dc2626; background: rgba(239,68,68,.08); }
.ks-info__btn--danger:hover { background: rgba(239,68,68,.15); }
.ks-info-enter-active, .ks-info-leave-active { transition: opacity .2s ease; }
.ks-info-enter, .ks-info-leave-to { opacity: 0; }

/* Desktop */
@media (min-width: 960px) {
  .km-support { position: fixed; top: 64px; left: 0; right: 0; bottom: 0; }
  .ks-top { padding: 14px 24px; }
  .ks-messages { padding: 20px max(16px, calc(50% - 480px)) 12px; }
  .ks-welcome { max-width: 640px; margin: 0 auto 20px; }
  .ks-topics { max-width: 640px; margin: 0 auto 20px; }
  .ks-bubble-wrap { max-width: 60%; }
  .ks-composer { padding: 14px max(16px, calc(50% - 480px)); }
}
</style>
